import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Buildings, CheckCircle, Factory, Truck } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FpShell } from "./FpShell";
import { fpBase, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const ICONS = { food_manufacturing: Factory, construction: Buildings, distribution: Truck };
const asText = (list) => (list || []).join(", ");
const asList = (text) => (text || "").split(",").map((s) => s.trim()).filter(Boolean);

/** One comma-separated list the consultant can trim before stamping the preset. */
const ListField = ({ label, hint, value, onChange, testid }) => (
  <div>
    <p className="label-caps">{label}</p>
    <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
              data-testid={testid}
              className="mt-2 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm text-navy outline-none focus:border-terracotta" />
  </div>
);

export default function FpWizard() {
  const { tenantId, ctx } = useFpContext();
  const { isPlatformAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [chosen, setChosen] = useState("");
  const [form, setForm] = useState(null);
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/industry-presets`);
      setData(out);
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const preset = (data?.presets || []).find((p) => p.key === chosen);

  const pick = (p) => {
    setChosen(p.key);
    setDone(null);
    setForm({
      functions: asText(p.shape.fixed.functions),
      production_categories: asText(p.shape.dimensions.production_categories),
      channels: asText(p.shape.dimensions.channels),
      streams: asText(p.shape.revenue.streams),
      components: asText(p.shape.dimensions.components),
    });
    setSources(p.sources.map((s) => s.code));
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data: out } = await api.post(
        `${fpBase(tenantId)}/industry-presets/${chosen}`,
        {
          sources,
          shape: {
            dimensions: {
              business_functions: asList(form.functions),
              production_categories: asList(form.production_categories),
              channels: asList(form.channels),
              components: asList(form.components),
            },
            fixed: { functions: asList(form.functions) },
            revenue: {
              streams: asList(form.streams),
              channels: asList(form.channels).map((name) => ({ name, sub_channels: [] })),
            },
          },
        },
      );
      setDone(out);
      toast.success(`Engagement set up — configuration version ${out.version}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <FpShell title="New engagement setup" showPeriod={false}
             description="Stamp the engine's shape for a new client in one pass: pick the industry closest to their business, trim the lists, and the configuration and statement list are written for them. Everything stays editable on Configuration afterwards.">
      {data?.error && (
        <div className="panel p-6 text-sm text-destructive" data-testid="fp-wizard-error">{data.error}</div>
      )}
      {!data && <p className="label-caps">Loading presets</p>}

      {data?.presets && (
        <div className="space-y-6" data-testid="fp-wizard">
          {!isPlatformAdmin && (
            <div className="panel bg-muted/40 p-4 text-xs text-muted-foreground"
                 data-testid="fp-wizard-readonly">
              Setting up a new engagement is an Aikyam platform-admin action. You can see the
              presets but not apply one.
            </div>
          )}

          <div className="panel p-6">
            <p className="label-caps">Step 1 · the client's industry</p>
            <h2 className="font-display text-lg text-navy">
              {ctx?.tenant_name ? `Which shape fits ${ctx.tenant_name}?` : "Which shape fits this client?"}
            </h2>
            {data.current?.version > 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid="fp-wizard-current">
                This engagement is already on configuration version {data.current.version}
                {data.current.preset ? ` (${data.current.preset} preset)` : ""} — applying a
                preset writes a new version, it never overwrites the old one.
              </p>
            )}
            <div className="mt-5 grid gap-3 lg:grid-cols-3">
              {data.presets.map((p) => {
                const Icon = ICONS[p.key] || Factory;
                return (
                  <button key={p.key} onClick={() => pick(p)} data-testid={`fp-wizard-preset-${p.key}`}
                          className={`rounded-2xl border p-5 text-left transition-colors ${
                            chosen === p.key ? "border-terracotta bg-terracotta/10"
                                             : "border-border bg-card hover:border-terracotta/50"}`}>
                    <Icon size={22} className="text-terracotta" />
                    <p className="mt-3 font-display text-base text-navy">{p.label}</p>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{p.hint}</p>
                    <p className="mt-3 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                      PVC by {p.pvc_basis.replace("_", " ")} · DVC by {p.dvc_basis} ·
                      {" "}{p.sources.length} statements
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {preset && form && (
            <>
              <div className="panel space-y-6 p-6" data-testid="fp-wizard-review">
                <div>
                  <p className="label-caps">Step 2 · trim the lists</p>
                  <h2 className="font-display text-lg text-navy">{preset.label}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Comma separated. Remove anything this client does not have and add what they
                    do — these become the values every ledger is classified through.
                  </p>
                </div>
                <div className="grid gap-6 lg:grid-cols-2">
                  <ListField label="Business functions" hint="Fixed cost is reported by these"
                             value={form.functions} testid="fp-wizard-functions"
                             onChange={(v) => setForm({ ...form, functions: v })} />
                  <ListField label="Production categories" hint="What production cost is counted by"
                             value={form.production_categories} testid="fp-wizard-categories"
                             onChange={(v) => setForm({ ...form, production_categories: v })} />
                  <ListField label="Delivery channels" hint="Across the revenue matrix"
                             value={form.channels} testid="fp-wizard-channels"
                             onChange={(v) => setForm({ ...form, channels: v })} />
                  <ListField label="Revenue streams" hint="Down the revenue matrix"
                             value={form.streams} testid="fp-wizard-streams"
                             onChange={(v) => setForm({ ...form, streams: v })} />
                  <div className="lg:col-span-2">
                    <ListField label="Cost components" hint="The components a ledger can carry"
                               value={form.components} testid="fp-wizard-components"
                               onChange={(v) => setForm({ ...form, components: v })} />
                  </div>
                </div>
              </div>

              <div className="panel p-6" data-testid="fp-wizard-sources">
                <p className="label-caps">Step 3 · the statements this client files</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Registered here, they appear on the Statements tab as what is still needed each
                  month. Untick anything this client will not supply.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {preset.sources.map((s) => {
                    const on = sources.includes(s.code);
                    return (
                      <button key={s.code} data-testid={`fp-wizard-source-${s.code}`}
                              onClick={() => setSources(on ? sources.filter((c) => c !== s.code)
                                                           : [...sources, s.code])}
                              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] transition-colors ${
                                on ? "border-terracotta bg-terracotta/10 text-navy"
                                   : "border-border bg-card text-muted-foreground hover:border-terracotta/50"}`}>
                        {on && <CheckCircle size={12} className="text-terracotta" />}
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border pt-5">
                  <button onClick={apply} disabled={busy || !isPlatformAdmin}
                          data-testid="fp-wizard-apply"
                          className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-white disabled:opacity-50">
                    {busy ? "Setting up" : "Set up this engagement"} <ArrowRight size={13} />
                  </button>
                  <p className="text-[11px] text-muted-foreground">
                    Writes one new configuration version and registers {sources.length} statements.
                  </p>
                </div>
              </div>
            </>
          )}

          {done && (
            <div className="panel border border-success/40 bg-success/5 p-6" data-testid="fp-wizard-done">
              <p className="label-caps">Done</p>
              <h2 className="font-display text-lg text-navy">
                Configuration version {done.version} written · {done.sources_registered.length} statements registered
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {done.functions.length} business functions and {done.categories.length} production
                categories are now the client's vocabulary. Next: upload this month's statements,
                then classify the ledgers.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/panchatanthra/financial-engine/sources" data-testid="fp-wizard-go-sources"
                      className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white">
                  Upload statements <ArrowRight size={12} />
                </Link>
                <Link to="/panchatanthra/financial-engine/setup" data-testid="fp-wizard-go-setup"
                      className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-muted">
                  Review configuration
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </FpShell>
  );
}
